import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import ContactModal from '../ContactModal';

// Pulse animation
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${({ theme }) => theme.primary || '#854CE6'}40;
  }
  70% {
    box-shadow: 0 0 0 10px transparent;
  }
  100% {
    box-shadow: 0 0 0 0 transparent;
  }
`;

// Floating button container
const FloatingButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${({ theme }) => theme.primary || '#854CE6'}, ${({ theme }) => theme.primary || '#854CE6'}DD);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  z-index: 999;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: ${pulse} 2s infinite;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: scale(1.1);
    animation: none;
    box-shadow: 0 8px 30px ${({ theme }) => theme.primary || '#854CE6'}60;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
    width: 55px;
    height: 55px;
    font-size: 1.3rem;
  }

  /* Hide on very small screens to avoid interference */
  @media (max-width: 480px) {
    display: none;
  }
`;

// Tooltip
const Tooltip = styled.div`
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.card || '#1C1E27'};
  color: ${({ theme }) => theme.text_primary || '#F2F3F4'};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: ${({ show }) => (show ? 1 : 0)};
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  border: 1px solid ${({ theme }) => theme.primary || '#854CE6'};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  &::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-left-color: ${({ theme }) => theme.primary || '#854CE6'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const FloatingContactButton = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Show button after user scrolls down a bit
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-show tooltip after 3 seconds, then hide after 3 more seconds
  useEffect(() => {
    if (isVisible) {
      const showTimer = setTimeout(() => {
        setShowTooltip(true);
      }, 3000);

      const hideTimer = setTimeout(() => {
        setShowTooltip(false);
      }, 6000);

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isVisible]);

  const handleClick = () => {
    setIsModalOpen(true);
    setShowTooltip(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <FloatingButton
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        aria-label="Contact Me"
      >
        💬
        <Tooltip show={showTooltip}>
          Let's talk!
        </Tooltip>
      </FloatingButton>
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
      />
    </>
  );
};

export default FloatingContactButton;
